import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"];
import { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIBadge from 'UIComponents/badge/UIBadge';
import UIButton from 'UIComponents/button/UIButton';
import UIButtonGroup from 'UIComponents/button/UIButtonGroup';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIList from 'UIComponents/list/UIList';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { ApprovalButton, useApprovalConditions } from 'ui-product-approvals';
import { openCommentTab, openApprovalsTab } from 'ContentEditorUI/redux/actions/collaborationSidebarActions';
// @ts-expect-error no declarations found for module
import { EditorApprovalContext } from './EditorApprovalProvider';
import useGetIsApprovalsEnabled from './useGetIsApprovalsEnabled';
import ApprovalAvailabilityTooltip from 'ContentEditorUI/components/approvals/ApprovalAvailabilityTooltip';
import { getHasUnrecoverableError } from 'ContentEditorUI/redux/selectors/collaborationSidebarSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getNumErrorsAndWarnings = ({
  errorsAndWarnings,
  errorsAndWarningsCount
}) => {
  if (errorsAndWarningsCount) {
    return errorsAndWarningsCount;
  }
  if (errorsAndWarnings) {
    return errorsAndWarnings.length;
  }
  return 0;
};
const EditorApprovalButtons = props => {
  const {
    children,
    onOpenWarningsAndSuggestionsPanel,
    errorsAndWarnings,
    isTestVariation = false,
    testVariationDisabledTooltip,
    dropdownProps,
    extraButtons,
    buttonSize,
    hasWriteAccessToProductApprovals,
    extraActionButtons,
    errorsAndWarningsCount
  } = props;
  const dispatch = useDispatch();
  const onOpenCommentTab = () => dispatch(openCommentTab());
  const onOpenApprovalsTab = () => dispatch(openApprovalsTab());
  const hasUnrecoverableError = useSelector(getHasUnrecoverableError);
  const {
    isFetchComplete,
    isPending,
    isCancelled,
    isApprover,
    isUserStepApproved,
    isNonParticipant,
    canUserTakeApprovableAction,
    isRequester
  } = useApprovalConditions();
  const isApprovalPendingOrCancelled = isPending || isCancelled;
  if (!isFetchComplete) {
    return /*#__PURE__*/_jsx("div", {
      className: "button-skeleton primary button-sm",
      "data-test-id": "loading-approval",
      children: /*#__PURE__*/_jsx("div", {
        className: "button-txt text-sm sk-fade",
        style: {
          width: '40px'
        }
      })
    });
  }
  if (canUserTakeApprovableAction) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  const approvalButton = isTestVariation ? /*#__PURE__*/_jsx(UITooltip, {
    title: testVariationDisabledTooltip,
    placement: "left",
    children: /*#__PURE__*/_jsx(ApprovalButton, {
      "data-test-id": "editor-approval-button",
      disabled: true,
      size: buttonSize
    }, 1)
  }) : /*#__PURE__*/_jsx(ApprovalButton, {
    "data-test-id": "editor-approval-button",
    disabled: !hasWriteAccessToProductApprovals,
    size: buttonSize
  }, 1);
  const viewApprovalButton = /*#__PURE__*/_jsx(ApprovalAvailabilityTooltip, {
    children: /*#__PURE__*/_jsx(UIButton, {
      "data-test-id": "editor-approval-view-approval-button",
      disabled: hasUnrecoverableError,
      onClick: onOpenApprovalsTab,
      use: "primary",
      size: buttonSize,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "approvals.viewApprovalActivity"
      })
    }, 3)
  });
  const shouldShowViewApprovalButtonAsMain = isPending && (isNonParticipant || isRequester || isUserStepApproved);
  const mainButton = shouldShowViewApprovalButtonAsMain ? viewApprovalButton : approvalButton;
  const numErrorsAndWarningsCount = getNumErrorsAndWarnings({
    errorsAndWarnings,
    errorsAndWarningsCount
  });
  return /*#__PURE__*/_jsxs(UIButtonGroup, {
    className: "display-flex",
    children: [mainButton, /*#__PURE__*/_jsx(UIDropdown, Object.assign({
      "data-test-id": "editor-approval-buttons-dropdown",
      buttonUse: "primary",
      buttonText: /*#__PURE__*/_jsx(UIIcon, {
        name: "down"
      }),
      buttonClassName: "p-x-1",
      caretRenderer: null,
      style: {
        borderLeft: `1px solid rgba(255, 255, 255, 0.4)`
      }
    }, dropdownProps, {
      children: /*#__PURE__*/_jsxs(UIList, {
        children: [extraActionButtons ? extraActionButtons.map(({
          label,
          onClick,
          id
        }, index) => /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": id,
          onClick: onClick,
          children: label
        }, index)) : extraButtons, isApprover && /*#__PURE__*/_jsx(ApprovalAvailabilityTooltip, {
          children: /*#__PURE__*/_jsx(UIButton, {
            "data-test-id": "editor-approval-request-changes-button",
            disabled: hasUnrecoverableError,
            onClick: onOpenCommentTab,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "approvals.requestChanges"
            })
          }, 2)
        }), isRequester && isPending && approvalButton, isApprovalPendingOrCancelled && !shouldShowViewApprovalButtonAsMain && viewApprovalButton, /*#__PURE__*/_jsxs(UIButton, {
          "data-test-id": "editor-approval-warnings-button",
          onClick: onOpenWarningsAndSuggestionsPanel,
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "approvals.warningsAndSuggestions"
          }), numErrorsAndWarningsCount > 0 && /*#__PURE__*/_jsx(UIBadge, {
            use: "count",
            children: numErrorsAndWarningsCount
          })]
        }, 4)]
      })
    }))]
  });
};
const EditorApprovalButtonsWrapper = props => {
  const {
      children
    } = props,
    editorApprovalButtonProps = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    hasReadAccessToProductApprovals,
    hasWriteAccessToProductApprovals
  } = useContext(EditorApprovalContext);
  const isApprovalsEnabled = useGetIsApprovalsEnabled();
  if (!hasReadAccessToProductApprovals || isApprovalsEnabled === false) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  return /*#__PURE__*/_jsx(EditorApprovalButtons, Object.assign({}, editorApprovalButtonProps, {
    hasWriteAccessToProductApprovals: hasWriteAccessToProductApprovals,
    children: children
  }));
};
export default EditorApprovalButtonsWrapper;